import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '@/api/client'
import { EP } from '@/api/endpoints'

const STORAGE_KEY = 'evx_currency_code'

export const useCurrencyStore = defineStore('currency', () => {
  // Active currency (used by format()).
  const code = ref('EUR')
  const symbol = ref('€')
  const rate = ref(1)

  // List of admin-enabled currencies for the switcher dropdown.
  // Each: { code, symbol, name, rate }
  const available = ref([])

  const loaded = ref(false)
  const availableLoaded = ref(false)

  // Convenience: lets header hide the switcher when only one option exists.
  const hasChoice = computed(() => available.value.length > 1)

  function applyCurrency(c) {
    if (!c) return
    code.value   = c.code   ?? code.value
    symbol.value = c.symbol ?? symbol.value
    rate.value   = (typeof c.rate === 'number' ? c.rate : Number(c.rate)) || 1
  }

  // Loads the default currency from publicSettings (legacy behavior — still
  // used as the initial pick when localStorage is empty AND the saved code
  // doesn't match an enabled currency).
  async function load() {
    if (loaded.value) return
    try {
      const r = await api.get(EP.publicSettings)
      const cur = r?.data?.currency
      if (cur) applyCurrency(cur)
    } catch {}
    loaded.value = true
  }

  // Loads the list of admin-enabled currencies and reconciles the active
  // selection against (a) localStorage and (b) the enabled list.
  async function loadAvailable() {
    if (availableLoaded.value) return
    try {
      const r = await api.get(EP.currencies)
      const list = Array.isArray(r?.data) ? r.data : []
      available.value = list

      const saved = (typeof localStorage !== 'undefined') ? localStorage.getItem(STORAGE_KEY) : null
      const fromSaved = saved ? list.find(c => c.code === saved) : null
      if (fromSaved) {
        applyCurrency(fromSaved)
      } else if (!list.find(c => c.code === code.value) && list.length > 0) {
        // Active currency was disabled in admin → fall back to first enabled.
        applyCurrency(list[0])
      }
    } catch {}
    availableLoaded.value = true
  }

  function select(newCode) {
    const next = available.value.find(c => c.code === newCode)
    if (!next) return
    applyCurrency(next)
    try { localStorage.setItem(STORAGE_KEY, next.code) } catch {}
  }

  function format(amount) {
    if (typeof amount !== 'number' || isNaN(amount)) return `${symbol.value}0.00`
    return `${symbol.value}${(amount * rate.value).toFixed(2)}`
  }

  return {
    code, symbol, rate,
    available, hasChoice,
    loaded, availableLoaded,
    load, loadAvailable, select, format
  }
})
